import axios from "axios";
import instance from "./BasebSurveysApi";
import params from "../configs/params";

/**
 * Get all survey templates for an organisation
 */
export function getSurveyTemplates(organisationId) {
  return instance.request({
    url: `templates/?organisation_id=${organisationId}`,
    method: "GET",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

/**
 * Get a single survey template by ID
 */
export function getSurveyTemplateDetails(templateId) {
  return instance.request({
    url: `templates/${templateId}/`,
    method: "GET",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

/**
 * Create a new survey template
 */
export function createSurveyTemplate(data) {
  // Check if data contains files (FormData) or is regular JSON
  const hasFiles = data instanceof FormData;
  
  if (hasFiles) {
    // For FormData, use axios directly to avoid any instance transformations
    const accessToken = localStorage.getItem("accessToken");
    return axios.post(`${params.bSurveysUrl}templates/`, data, {
      headers: {
        Accept: "application/json",
        "Content-Type": "multipart/form-data",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      },
    });
  }
  
  return instance.request({
    url: `templates/`,
    method: "POST",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data: data,
  });
}

/**
 * Update an existing survey template
 */
export function updateSurveyTemplate(templateId, data) {
  // Check if data contains files (FormData) or is regular JSON
  const hasFiles = data instanceof FormData;
  
  if (hasFiles) {
    // For FormData, use axios directly to avoid any instance transformations
    const accessToken = localStorage.getItem("accessToken");
    return axios.patch(`${params.bSurveysUrl}templates/${templateId}/`, data, {
      headers: {
        Accept: "application/json",
        "Content-Type": "multipart/form-data",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      },
    });
  }
  
  return instance.request({
    url: `templates/${templateId}/`,
    method: "PATCH",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data: data,
  });
}

/**
 * Delete a survey template
 */
export function deleteSurveyTemplate(templateId) {
  return instance.request({
    url: `templates/${templateId}/`,
    method: "DELETE",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

// =============================================================================
// SURVEY RESPONSES API
// =============================================================================

export function getSurveyResponses(organisationId, options = {}) {
  const params = new URLSearchParams();
  params.append('organisation_id', organisationId);
  
  if (options.templateId) {
    params.append('template_id', options.templateId);
  }
  if (options.search) {
    params.append('search', options.search);
  }
  if (options.limit) {
    params.append('limit', options.limit);
  }
  if (options.offset) {
    params.append('offset', options.offset);
  }
  
  return instance.request({
    url: `responses/?${params.toString()}`,
    method: "GET",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

/**
 * Get a single survey response with all answers
 */
export function getSurveyResponseDetails(responseId) {
  return instance.request({
    url: `responses/${responseId}/`,
    method: "GET",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

/**
 * Get aggregated statistics for survey responses
 * @param {string} organisationId - The organisation ID
 * @param {string} templateId - Optional: Filter by template UUID
 */
export function getSurveyResponseStats(organisationId, templateId = null) {
  const params = new URLSearchParams();
  params.append('organisation_id', organisationId);
  
  if (templateId) {
    params.append('template_id', templateId);
  }
  
  return instance.request({
    url: `responses/stats/?${params.toString()}`,
    method: "GET",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

// =============================================================================
// REWARD CODES API
// =============================================================================

/**
 * Get all reward codes for a survey template
 * @param {string} templateId - The template UUID
 */
export function getRewardCodes(templateId) {
  return instance.request({
    url: `templates/${templateId}/rewards/`,
    method: "GET",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

/**
 * Create a new reward code for a template
 * @param {string} templateId - The template UUID
 * @param {object} data - Reward code data
 */
export function createRewardCode(templateId, data) {
  return instance.request({
    url: `templates/${templateId}/rewards/`,
    method: "POST",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data: data,
  });
}

/**
 * Update a reward code
 * @param {string} templateId - The template UUID
 * @param {number} rewardId - The reward code ID
 * @param {object} data - Updated reward code data
 */
export function updateRewardCode(templateId, rewardId, data) {
  return instance.request({
    url: `templates/${templateId}/rewards/${rewardId}/`,
    method: "PATCH",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data: data,
  });
}

/**
 * Delete a reward code
 * @param {string} templateId - The template UUID
 * @param {number} rewardId - The reward code ID
 */
export function deleteRewardCode(templateId, rewardId) {
  return instance.request({
    url: `templates/${templateId}/rewards/${rewardId}/`,
    method: "DELETE",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

// =============================================================================
// REWARD CAMPAIGN API
// =============================================================================

/**
 * Get reward campaign settings for a template
 * @param {string} templateId - The template UUID
 */
export function getRewardCampaign(templateId) {
  return instance.request({
    url: `templates/${templateId}/reward-campaign/`,
    method: "GET",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

/**
 * Update reward campaign settings for a template
 * @param {string} templateId - The template UUID
 * @param {object} data - Campaign settings data
 */
export function updateRewardCampaign(templateId, data) {
  return instance.request({
    url: `templates/${templateId}/reward-campaign/`,
    method: "PUT",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data: data,
  });
}

// =============================================================================
// REWARD CLAIMS API
// =============================================================================

/**
 * Get reward claims for a template
 * @param {string} templateId - The template UUID
 * @param {object} options - Query options
 * @param {string} options.email - Filter by email
 * @param {number} options.rewardId - Filter by reward ID
 * @param {number} options.limit - Limit results
 * @param {number} options.offset - Offset for pagination
 */
export function getRewardClaims(templateId, options = {}) {
  const params = new URLSearchParams();
  
  if (options.email) {
    params.append('email', options.email);
  }
  if (options.rewardId) {
    params.append('reward_id', options.rewardId);
  }
  if (options.limit) {
    params.append('limit', options.limit);
  }
  if (options.offset) {
    params.append('offset', options.offset);
  }
  
  const queryString = params.toString();
  const url = queryString 
    ? `templates/${templateId}/reward-claims/?${queryString}`
    : `templates/${templateId}/reward-claims/`;
  
  return instance.request({
    url,
    method: "GET",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

/**
 * Get dashboard settings for the current user
 * @param {number} organisationId - The organisation ID
 */
export function getDashboardSettings(organisationId) {
  return instance.request({
    url: `dashboard-settings/?organisation_id=${organisationId}`,
    method: "GET",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

/**
 * Update dashboard settings for the current user
 * @param {number} organisationId - The organisation ID
 * @param {object} data - The settings data to update
 */
export function updateDashboardSettings(organisationId, data) {
  return instance.request({
    url: `dashboard-settings/`,
    method: "PUT",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data: {
      organisation_id: organisationId,
      ...data,
    },
  });
}
